import { useTranslation } from "react-i18next";
import { TrashIcon } from "@heroicons/react/outline";
import type { RagAgent } from "../api/ragApi";
import WixarBadge from "./WixarBadge";

const AGENT_ICONS: Record<string, string> = {
  default: "\uD83E\uDD16",
  book: "\uD83D\uDCDA",
  support: "\uD83C\uDFA7",
  brain: "\uD83E\uDDE0",
  search: "\uD83D\uDD0D",
  chat: "\uD83D\uDCAC",
  star: "\u2B50",
  tool: "\uD83D\uDD27",
  idea: "\uD83D\uDCA1",
  language: "\uD83C\uDF0D",
  education: "\uD83C\uDF93",
  expert: "\uD83D\uDC54",
};

interface AgentCardProps {
  agent: RagAgent;
  onClick?: () => void;
  onDelete?: (agentId: number) => void;
}

const AgentCard = ({ agent, onClick, onDelete }: AgentCardProps) => {
  const { t } = useTranslation();
  const isClickable = !!onClick;

  return (
    <div
      onClick={isClickable ? onClick : undefined}
      className={`group/card bg-white rounded-xl border p-4 shadow-sm transition-all duration-200 text-left w-full flex flex-col justify-between h-full ${
        isClickable
          ? "cursor-pointer hover:shadow-md hover:border-green-500"
          : "cursor-default opacity-90"
      } ${agent.is_shared ? "border-blue-200" : "border-gray-200"}`}
    >
      <div className="flex items-start justify-between gap-3 w-full">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{agent.name}</h3>
            {agent.is_shared && <WixarBadge />}
          </div>
          <p
            className={`text-xs mt-1 line-clamp-2 ${
              agent.description ? "text-gray-500" : "text-gray-400 italic"
            }`}
          >
            {agent.description || t("pages.agents.noDescription")}
          </p>
        </div>
        <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
          <span className="text-lg leading-none">
            {AGENT_ICONS[agent.icon] || AGENT_ICONS.default}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1.5">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <span className="text-xs text-gray-500">
            {t("pages.agents.documentCount", { count: agent.document_count ?? 0 })}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
            />
          </svg>
          <span className="text-xs text-gray-500">
            {t("pages.agents.chunkCount", { count: agent.total_chunks ?? 0 })}
          </span>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {agent.created_at && (
            <span className="text-xs text-gray-400">
              {new Date(agent.created_at).toLocaleDateString()}
            </span>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(agent.id);
              }}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              aria-label={t("pages.agents.deleteAgent")}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentCard;
